import React from 'react';
import { Menu, MenuItem, CircularProgress, Link as MuiLink } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IHelpButtonProps } from './IHelpButton';

interface DocumentItem {
    name: string;
    url: string;
}

interface DocumentData {
    translationKey: string;
    url: string;
}

const documentData: DocumentData[] = [
    { translationKey: 'header_help_button_user_guide_macro1', url: '/documents/M1_User_Guide_rev01_2024.pdf' },
    { translationKey: 'header_help_button_user_guide_macro2', url: '/documents/M2_User_Guide_rev02_2024.pdf' },
    { translationKey: 'header_help_button_reference_manual_macro1', url: '/documents/MACRO1_Theory_and_Background_draft_rev.00_2024.pdf' },
    { translationKey: 'header_help_button_reference_manual_macro2', url: '/documents/M2_Reference_Manual_rev02_2024.pdf' },
    { translationKey: 'header_label_contact', url: 'https://edesign.maccaferri.com/Gestione?code=contactus&appID=36' },
];

const HelpButton: React.FC<IHelpButtonProps> = (props) => {
    const { t, i18n } = useTranslation();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [documents, setDocuments] = React.useState<DocumentItem[]>([]);
    const [error, setError] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState<boolean>(false);

    // Funzione per caricare tutti i documenti
    const fetchAllDocuments = React.useCallback(() => {
        setLoading(true);
        try {
            const docs = documentData.map((doc) => ({
                name: t(doc.translationKey),
                url: doc.url,
            }));
            setDocuments(docs);
            setLoading(false);
        } catch (e) {
            console.error("Error loading documents:", e);
            setError(t('error_loading_documents'));
            setLoading(false);
        }
    }, [t]);

    // Funzione per aggiornare i nomi dei documenti in base alla lingua
    const updateDocumentNames = React.useCallback(() => {
        setDocuments((prevDocs) =>
            prevDocs.map((doc, index) => ({
                ...doc,
                name: t(documentData[index].translationKey),
            })),
        );
    }, [t]);

    // Gestore del click sul bottone
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(e.currentTarget);
        if (documents.length === 0 && !loading) {
            fetchAllDocuments();
        } else {
            updateDocumentNames();
        }
    };

    // Gestore della chiusura del menu
    const handleClose = () => {
        setAnchorEl(null);
    };

    // Effetto per aggiornare i nomi dei documenti quando cambia la lingua
    React.useEffect(() => {
        if (i18n.isInitialized && documents.length > 0) {
            updateDocumentNames();
        }
    }, [i18n.language, i18n.isInitialized, documents.length, updateDocumentNames]);

    return (
        <>
            <button
                type="button"
                className={props.className}
                onClick={handleClick}
                aria-haspopup="true"
                aria-expanded={open}
                aria-controls="basic-menu-help"
            >
                <i className="fa-solid fa-question" />
                {t('header_help_button')}
                <i className="fa-solid fa-angle-down" />
            </button>
            <Menu
                id="basic-menu-help"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'help-button',
                }}
            >
                {loading ? (
                    <MenuItem disabled>
                        <CircularProgress size={24} style={{ marginRight: 8 }} />
                        {t('loading', 'loading...')}
                    </MenuItem>
                ) : error ? (
                    <MenuItem disabled>{error}</MenuItem>
                ) : documents.length > 0 ? (
                    documents.map((doc, index) => (
                        <MenuItem key={`doc-${index}`} onClick={handleClose}>
                            <MuiLink href={doc.url} target="_blank" rel="noopener noreferrer" underline="none" color="inherit">
                                {doc.name}
                            </MuiLink>
                        </MenuItem>
                    ))
                ) : (
                    <MenuItem disabled>{t('no_documents_available', 'Nessun documento disponibile')}</MenuItem>
                )}
            </Menu>
        </>
    );
};

export default React.memo(HelpButton);
